import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  CartTotalsService,
  CheckoutObject,
  Customer,
  MetadataService,
  NavbarCartItem,
  ProductService,
  TotalObject
} from 'gung-standard';
import { Observable, Subject, first, map, switchMap, takeUntil } from 'rxjs';
import { CheckoutSettingsGroup, HlDisplayPortalFeaturesService } from './hl-display-portal-features.service';
import { HLDisplayCheckoutUtilService } from './hldisplay-checkout-util.service';

@Injectable({
  providedIn: 'root'
})
export class HlDisplayAdditionalCostsService {
  cartTotal: TotalObject;
  skipAdminCharge = false;
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private cartTotalsService: CartTotalsService,
    private productService: ProductService,
    private http: HttpClient,
    private hlDisplayPortalFeaturesService: HlDisplayPortalFeaturesService,
    @Optional()
    @Inject('environment')
    protected environment: { [s: string]: any },
    private hlDisplayCheckoutUtilService: HLDisplayCheckoutUtilService,
  ) {
    this.cartTotalsService
      .getTotals()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(totalsObject => {
        this.cartTotal = totalsObject;
      });
    this.hlDisplayPortalFeaturesService
      .getPortalFeatureGroup('orderCheckoutSettingsGroup')
      .pipe(
        first(),
        map(portalFeatureGroup => {
          return portalFeatureGroup as CheckoutSettingsGroup;
        })
      )
      .subscribe(checkoutSettingsGroup => {
        this.hlDisplayCheckoutUtilService.emitSkipAdminFeeEvent(!checkoutSettingsGroup.applyAdminCharge);
        this.skipAdminCharge = !checkoutSettingsGroup.applyAdminCharge;
      });
  }

  getAdditionalCostTerms(customer: Customer): Observable<HlDisplayAdditionalCostTerms> {
    return this.http.get<HlDisplayAdditionalCostTerms>(`json/hl-display/get-trade-policy/${customer.id}`).pipe(first());
  }

  getFreightCharge(checkoutTotals: number, additionalCostTermsObject: HlDisplayAdditionalCostTerms) {
    let freightCostValue = null;

    if (!additionalCostTermsObject) {
      return freightCostValue;
    }

    // Customer Portal Pharmacie: Freight charge (€25) should be placed on article: 900000 when the order is under €300
    freightCostValue = {
      productId: '900000',
      value: additionalCostTermsObject.freight,
      quantity: 1,
      freightMin: additionalCostTermsObject.freightMin
    };
    return freightCostValue;
  }

  getAdminFee(checkoutTotals: number, additionalCostTermsObject: HlDisplayAdditionalCostTerms) {
    let adminCostValue = null;

    if (!additionalCostTermsObject || this.skipAdminCharge) {
      return adminCostValue;
    }

    let adminChargeToUse = additionalCostTermsObject.adminCharge;

    // Usage of == vs === is important here. We need to check for both null and undefined which == handles.
    if (adminChargeToUse == null) {
      // Before this was the default, but we needed to be able to put 0 (without it defaulting to 17).
      // This was the solution to this.

      // Customer Portal Pharmacie: Admin (€17) should be placed on article: 527859 when the order is under €300
      adminChargeToUse = 17;
    }

    adminCostValue = {
      productId: '527859',
      value: adminChargeToUse,
      quantity: 1,
      adminChargeMin: additionalCostTermsObject.adminChargeMin
    };

    return adminCostValue;
  }

  getMinOrder(additionalCostTermsObject: HlDisplayAdditionalCostTerms) {
    if (additionalCostTermsObject.minOrderQty <= 0) {
      return null;
    }
    const minOrder = {
      value: additionalCostTermsObject.minOrderQty
    };

    return minOrder;
  }

  getCurrencySymbol(currencyCode: string) {
    return (0)
      .toLocaleString(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
      .replace(/\d/g, '')
      .trim();
  }

  addFreightChargeToCheckout(
    checkoutObject: CheckoutObject,
    additionalCostTermsObject: HlDisplayAdditionalCostTerms,
    currencyCode: string
  ) {
    let freightCostValue = null;

    if (this.cartTotal.totalValue < additionalCostTermsObject.freightMin) {
      // Customer Portal Pharmacie: Freight charge (€25) should be placed on article: 900000 when the order is under €300
      freightCostValue = {
        productId: '900000',
        value: additionalCostTermsObject.freight,
        currencyCode: currencyCode,
        quantity: 1,
        freightMin: additionalCostTermsObject.freightMin,
        currencySymbol: this.getCurrencySymbol(currencyCode)
      };

      checkoutObject.extra.additionalCosts = {
        ...checkoutObject.extra.additionalCosts,
        freight: freightCostValue
      };
    }
  }

  addAdminFeeToCheckout(
    checkoutObject: CheckoutObject,
    additionalCostTermsObject: HlDisplayAdditionalCostTerms,
    currencyCode: string
  ) {
    let adminCostValue = null;

    if (this.skipAdminCharge) {
      return;
    }

    if (this.cartTotal.totalValue < additionalCostTermsObject.adminChargeMin) {
      let adminChargeToUse = additionalCostTermsObject.adminCharge;

      // Usage of == vs === is important here. We need to check for both null and undefined which == handles.
      if (adminChargeToUse == null) {
        // Before this was the default, but we needed to be able to put 0 (without it defaulting to 17).
        // This was the solution to this.

        // Customer Portal Pharmacie: Admin (€17) should be placed on article: 527859 when the order is under €300
        adminChargeToUse = 17;
      }

      adminCostValue = {
        productId: '527859',
        value: adminChargeToUse,
        currencyCode: currencyCode,
        quantity: 1,
        adminChargeMin: additionalCostTermsObject.adminChargeMin,
        currencySymbol: this.getCurrencySymbol(currencyCode)
      };

      checkoutObject.extra.additionalCosts = {
        ...checkoutObject.extra.additionalCosts,
        adminFee: adminCostValue
      };
    }
  }

  addMinOrderToCheckout(
    checkoutObject: CheckoutObject,
    additionalCostTermsObject: HlDisplayAdditionalCostTerms,
    currencyCode: string
  ): void {
    if (additionalCostTermsObject.minOrderQty > 0) {
      const minOrder = {
        value: additionalCostTermsObject.minOrderQty,
        currencyCode: currencyCode,
        currencySymbol: this.getCurrencySymbol(currencyCode)
      };

      checkoutObject.extra.additionalCosts = {
        ...checkoutObject.extra.additionalCosts,
        minOrder: minOrder
      };
    }
  }

  applyTradePolicyToCheckout(checkoutObject: CheckoutObject): Observable<CheckoutObject> {
    console.log('Applying trade policy to checkout', checkoutObject);
    return this.http.post<CheckoutObject>('json/hl-display/apply-trade-policy-to-order', checkoutObject).pipe(first());
  }

  // Add the broken box fee to the order total
  // The broken box fee is calculated by checking if the quantity of the product is a multiple of the quantity in the box
  // If it is not a multiple, the broken box fee is added to the order total
  public calculateBrokenBoxFee(cartRows: NavbarCartItem[], brokenBoxFeeBaseCost: number): number {
    let brokenBoxFee = 0;
    cartRows.forEach(cartRow => {
      if (
        !!!cartRow.product.extra.ar?.q_hl_sales_qty ||
        cartRow.product.extra.ar.q_hl_sales_qty === 0 ||
        cartRow.cartRow.extra?._skipBrokenBoxFee
      ) {
        return;
      }
      // Check if the quantity of the product is a multiple of the quantity in the box
      if (cartRow.product.extra.ar.q_hl_sales_qty === 0) {
        return;
      }

      if (cartRow.cartRow.qty % cartRow.product.extra.ar.q_hl_sales_qty === 0) {
        return;
      }

      brokenBoxFee += brokenBoxFeeBaseCost;
    });

    return brokenBoxFee;
  }
}

export class HlDisplayAdditionalCostTerms {
  freight: number;
  minOrderQty: number;
  adminCharge: number;
  franco: number;
  freightMin: number;
  adminChargeMin: number;
  brokenBoxFee: number;
}
